import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import { useSnackbar } from 'notistack';
import invitationService from './invitationService';
import accountService from '../../services/accountService';
import authService from '../../services/authService';
import {
  brandAssetUrl,
  brandConfig,
} from '../../branding/brandConfig';

const PENDING_INVITE_CODE_KEY = 'pendingInvitationCode';
const INVITE_FLOW_DONE_KEY = 'invitationFlowDone';

type Preview = {
  email?: string;
  tenantName?: string;
  needsRegistration?: boolean;
  accountExists?: boolean;
  hasPassword?: boolean;
  alreadyAccepted?: boolean;
  message?: string;
};

const AcceptInvitationPage = (props: { onLocalAuthenticated?: () => void }) => {
  const [searchParams] = useSearchParams();
  const codeFromUrl = useMemo(
    () => (searchParams.get('code') ?? '').trim(),
    [searchParams]
  );
  const [code, setCode] = useState(codeFromUrl);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const loggedIn = authService.hasLocalToken();

  useEffect(() => {
    const resolved =
      codeFromUrl ||
      (() => {
        try {
          return sessionStorage.getItem(PENDING_INVITE_CODE_KEY) ?? '';
        } catch {
          return '';
        }
      })();
    if (resolved) {
      setCode(resolved);
      try {
        sessionStorage.setItem(PENDING_INVITE_CODE_KEY, resolved);
      } catch {
        // ignore
      }
    }
  }, [codeFromUrl]);

  useEffect(() => {
    const trimmed = code.trim();
    if (!trimmed) {
      setPreview(null);
      setPreviewError('');
      return;
    }

    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError('');
    invitationService.preview(trimmed).then((response) => {
      if (cancelled) return;
      setLoadingPreview(false);
      if (response.hasError) {
        setPreview(null);
        setPreviewError(
          (response.errorMessages ?? []).join(', ') || 'Érvénytelen meghívó.'
        );
        return;
      }
      setPreview(response.result as Preview);
    });
    return () => {
      cancelled = true;
    };
  }, [code]);

  const goHome = () => {
    try {
      sessionStorage.removeItem(PENDING_INVITE_CODE_KEY);
      sessionStorage.setItem(INVITE_FLOW_DONE_KEY, '1');
    } catch {
      // ignore
    }
    window.location.hash = '#/';
    window.location.reload();
  };

  const accept = async () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    if (preview?.alreadyAccepted) {
      goHome();
      return;
    }

    const needsPassword =
      !loggedIn &&
      (preview?.needsRegistration ||
        (preview?.accountExists && preview?.hasPassword));

    if (!loggedIn && preview?.needsRegistration) {
      if (!password || password.length < 8) {
        enqueueSnackbar('A jelszó legalább 8 karakter legyen.', {
          variant: 'warning',
        });
        return;
      }
      if (password !== passwordConfirm) {
        enqueueSnackbar('A két jelszó nem egyezik.', { variant: 'warning' });
        return;
      }
    }

    if (
      !loggedIn &&
      preview?.accountExists &&
      preview?.hasPassword &&
      !password
    ) {
      enqueueSnackbar('Add meg a jelszavad, vagy jelentkezz be a főoldalon.', {
        variant: 'info',
      });
      return;
    }

    setSubmitting(true);
    dispatch({ type: 'SHOW_SAVE' });
    try {
      const response = await invitationService.accept({
        code: trimmed,
        password:
          needsPassword || preview?.needsRegistration ? password : undefined,
        fullName: preview?.needsRegistration ? fullName : undefined,
      });

      if (response.hasError) {
        const msg = (response.errorMessages ?? []).join(',');
        const accountExists = !!(response as any).result?.accountExists;
        if (accountExists || response.status === 401) {
          enqueueSnackbar(
            msg ||
              'Jelentkezz be a meglévő fiókoddal, majd fogadd el a meghívót.',
            { variant: 'warning' }
          );
          return;
        }
        enqueueSnackbar(msg || 'A meghívó elfogadása sikertelen.', {
          variant: 'error',
        });
        return;
      }

      const raw = response.result ?? {};
      const token =
        raw.accessToken ?? raw.AccessToken ?? raw.access_token ?? null;
      const refresh =
        raw.refreshToken ?? raw.RefreshToken ?? raw.refresh_token ?? null;
      if (token) {
        authService.persistLocalSession(token, refresh);
        props.onLocalAuthenticated?.();
      }

      try {
        sessionStorage.removeItem(PENDING_INVITE_CODE_KEY);
        sessionStorage.setItem(INVITE_FLOW_DONE_KEY, '1');
      } catch {
        // ignore
      }

      setDone(true);
      enqueueSnackbar('Meghívó elfogadva', { variant: 'success' });

      if (token) {
        try {
          const userInfo = await accountService.getUserInfo();
          dispatch({ type: 'LOAD_USER_INFO', payload: userInfo });
        } catch {
          // ignore — still navigate with the new session
        }
      }

      // Leave the invitation-only App shell and avoid pending-code bounce.
      window.location.hash = '#/';
      window.location.reload();
    } finally {
      setSubmitting(false);
      dispatch({ type: 'HIDE' });
    }
  };

  const alreadyAccepted = !!preview?.alreadyAccepted;
  const showCodeField = !codeFromUrl;
  const isTerminal = alreadyAccepted || done || !!previewError;
  const showRegistrationFields =
    !!preview?.needsRegistration && !done && !alreadyAccepted && !loggedIn;
  const showLoginPassword =
    !!preview?.accountExists &&
    !!preview?.hasPassword &&
    !done &&
    !alreadyAccepted &&
    !loggedIn;

  const primaryLabel = alreadyAccepted
    ? 'Bejelentkezés'
    : preview?.needsRegistration && !loggedIn
      ? 'Fiók létrehozása és csatlakozás'
      : 'Meghívó elfogadása';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        px: 2,
        py: 3,
        boxSizing: 'border-box',
      }}
    >
      <Paper elevation={3} sx={{ p: '2rem', width: 420, maxWidth: '95%' }}>
        <Box sx={{ textAlign: 'center', mb: 2.5 }}>
          <img
            src={brandAssetUrl(brandConfig.assets.logoSvg)}
            alt={brandConfig.logoAlt}
            width={112}
            height={112}
            style={{ marginBottom: '0.75rem', objectFit: 'contain' }}
          />
          <Typography variant="h6" component="h1" gutterBottom>
            {alreadyAccepted
              ? 'Meghívó már elfogadva'
              : done
                ? 'Sikeres csatlakozás'
                : previewError
                  ? 'Érvénytelen meghívó'
                  : 'Meghívó elfogadása'}
          </Typography>
          {preview?.tenantName && !previewError ? (
            <Typography variant="body2" color="text.secondary">
              {preview.tenantName}
              {preview.email ? ` · ${preview.email}` : ''}
            </Typography>
          ) : null}
        </Box>

        {loadingPreview ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {previewError ? (
              <Alert severity="error">{previewError}</Alert>
            ) : null}

            {alreadyAccepted && !done ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', mb: 0.5 }}
              >
                Ez a meghívó már fel lett használva.
                {preview?.email
                  ? ' Jelentkezz be a fenti email címmel.'
                  : ' Jelentkezz be a kezdőlapon.'}
              </Typography>
            ) : null}

            {preview && !done && !alreadyAccepted ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center' }}
              >
                {preview.needsRegistration
                  ? 'Hozz létre jelszót a fiókodhoz, majd csatlakozz.'
                  : loggedIn
                    ? 'Fogadd el a meghívót a csatlakozáshoz.'
                    : 'Add meg a jelszavad, vagy Microsoft fiókkal jelentkezz be a kezdőlapon.'}
              </Typography>
            ) : null}

            {done ? (
              <Alert severity="success">
                Meghívó elfogadva. Átirányítás a CMS-be…
              </Alert>
            ) : null}

            {showCodeField ? (
              <TextField
                fullWidth
                label="Meghívó kód"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={done}
                autoFocus={!codeFromUrl}
              />
            ) : null}

            {showRegistrationFields ? (
              <>
                <TextField
                  fullWidth
                  label="Megjelenített név"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={preview?.email}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Jelszó (min. 8 karakter)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Jelszó megerősítése"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  autoComplete="new-password"
                  onKeyDown={(e) => e.key === 'Enter' && accept()}
                />
              </>
            ) : null}

            {showLoginPassword ? (
              <TextField
                fullWidth
                type="password"
                label="Jelszó"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                onKeyDown={(e) => e.key === 'Enter' && accept()}
              />
            ) : null}

            {alreadyAccepted || done ? (
              <Button variant="contained" fullWidth onClick={goHome}>
                Bejelentkezés
              </Button>
            ) : previewError ? (
              <Button variant="contained" fullWidth onClick={goHome}>
                Vissza a belépéshez
              </Button>
            ) : (
              <Button
                variant="contained"
                fullWidth
                disabled={!code.trim() || submitting || loadingPreview}
                onClick={accept}
              >
                {primaryLabel}
              </Button>
            )}

            {!isTerminal ? (
              <Button
                variant="text"
                onClick={() => navigate('/')}
                sx={{ alignSelf: 'center' }}
              >
                Kezdőlap
              </Button>
            ) : null}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AcceptInvitationPage;

export { PENDING_INVITE_CODE_KEY, INVITE_FLOW_DONE_KEY };
